import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { RouterLink } from '@angular/router';

interface PropertyItem {
  id: number;
  code: string;
  name: string;
  vmApiBaseUrl: string;
  isActive: boolean;
}

@Component({
  selector: 'app-properties',
  imports: [FormsModule, RouterLink],
  template: `
    <section class="card">
      <header class="head">
        <div>
          <h1>Choose Property Context</h1>
          <p>Select the property you are working on. Switching property ends VM session automatically.</p>
        </div>
        <div class="actions">
          @if (activePropertyId()) {
            <span class="badge">Current: {{ activePropertyCode() }}</span>
          }
          <button type="button" class="secondary" routerLink="/settings/property-administration">Property Administration</button>
        </div>
      </header>

      @if (msg()) {
        <div class="msg" [class.err]="err()">{{ msg() }}</div>
      }

      <div class="search">
        <label for="property-search">Find Property</label>
        <input
          id="property-search"
          [(ngModel)]="search"
          name="search"
          placeholder="Search by property code or name"
          (ngModelChange)="onSearchChange()"
        />
      </div>

      <div class="toolbar">
        <span class="muted">Showing {{ properties().length }} of {{ total() }} matching properties</span>
        <div class="pager">
          <button type="button" class="secondary" (click)="prevPage()" [disabled]="busy() || page() <= 1">Prev</button>
          <span>Page {{ page() }} / {{ totalPages() }}</span>
          <button type="button" class="secondary" (click)="nextPage()" [disabled]="busy() || page() >= totalPages()">Next</button>
        </div>
      </div>

      <div class="list">
        @for (p of properties(); track p.id) {
          <article class="row" [class.selected]="activePropertyId() === p.id">
            <div>
              <strong>{{ p.name }}</strong>
              <div class="muted"><code>{{ p.code }}</code> · {{ p.vmApiBaseUrl }}</div>
            </div>
            <div class="row-actions">
              <span class="badge" [class.badge-off]="!p.isActive">{{ p.isActive ? 'Active' : 'Inactive' }}</span>
              <button type="button" (click)="select(p.id)" [disabled]="busy() || !p.isActive || activePropertyId() === p.id">
                {{ activePropertyId() === p.id ? 'Selected' : 'Use This Property' }}
              </button>
            </div>
          </article>
        } @empty {
          <div class="empty">No properties match this search.</div>
        }
      </div>
    </section>
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px;display:grid;gap:14px;max-width:980px}
    .head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap}
    .head p{margin:4px 0 0;color:var(--muted)}
    .actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
    .search{display:grid;gap:6px}
    .search label{font-size:12px;font-weight:700;color:#4e5f7f;text-transform:uppercase;letter-spacing:.04em}
    .search input{height:40px;border:1px solid #c8d2e1;border-radius:8px;padding:0 12px}
    .toolbar{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
    .pager{display:flex;align-items:center;gap:8px}
    .list{display:grid;gap:8px}
    .row{display:flex;justify-content:space-between;align-items:center;gap:12px;border:1px solid #dde5f1;border-radius:10px;padding:10px 12px;background:#fff}
    .row.selected{border-color:#7aa3e6;background:#eef4ff}
    .row-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
    .msg{padding:10px;border-radius:10px;background:#eef4ff;border:1px solid #c7d7f5;color:#0a2f6b}
    .msg.err{background:#fdecec;border-color:#f2c4c4;color:#8a1f1f}
    .muted{color:var(--muted)}
    .badge{padding:4px 10px;border-radius:999px;background:#e7f6ec;color:#1a6b40;font-size:12px;font-weight:700}
    .badge-off{background:#f6e7e7;color:#8a1f1f}
    .empty{padding:14px;border:1px dashed #c8d2e1;border-radius:10px;color:var(--muted)}
    button{height:36px;border:0;border-radius:8px;background:var(--primary);color:#fff;padding:0 10px;font-weight:700}
    .secondary{background:#6b778c}
    @media (max-width: 760px){
      .row{flex-direction:column;align-items:flex-start}
      .row-actions{width:100%;justify-content:space-between}
    }
  `,
})
export class PropertiesComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly busy = signal(false);
  readonly msg = signal('');
  readonly err = signal(false);
  readonly properties = signal<PropertyItem[]>([]);
  readonly total = signal(0);
  readonly take = signal(20);
  readonly page = signal(1);
  readonly activePropertyId = signal<number>(0);
  readonly activePropertyCode = signal('');
  search = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit(): Promise<void> {
    await this.refreshContext();
    await this.loadPage();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.take()));
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      void this.loadPage();
    }, 180);
  }

  async prevPage(): Promise<void> {
    if (this.page() <= 1) return;
    this.page.set(this.page() - 1);
    await this.loadPage();
  }

  async nextPage(): Promise<void> {
    if (this.page() >= this.totalPages()) return;
    this.page.set(this.page() + 1);
    await this.loadPage();
  }

  private async refreshContext(): Promise<void> {
    try {
      const ctx = await firstValueFrom(this.api.get<{ activeProperty: { id: number; code: string } | null }>('/context/property'));
      this.activePropertyId.set(Number(ctx.activeProperty?.id ?? 0) || 0);
      this.activePropertyCode.set(String(ctx.activeProperty?.code ?? '').trim());
    } catch {
      this.activePropertyId.set(0);
      this.activePropertyCode.set('');
    }
  }

  async loadPage(): Promise<void> {
    this.busy.set(true);
    try {
      const skip = (this.page() - 1) * this.take();
      const q = encodeURIComponent(this.search.trim());
      const props = await firstValueFrom(
        this.api.get<{ items: PropertyItem[]; total: number; take: number; skip: number }>(
          `/properties?q=${q}&take=${this.take()}&skip=${skip}&includeInactive=false`,
        ),
      );
      this.properties.set(props.items || []);
      this.total.set(Number(props.total ?? 0) || 0);
    } finally {
      this.busy.set(false);
    }
  }

  async select(propertyId: number): Promise<void> {
    this.busy.set(true);
    this.err.set(false);
    this.msg.set('');
    try {
      await firstValueFrom(this.api.post('/context/property/select', { propertyId }));
      await this.refreshContext();
      this.msg.set('Property context selected. VM login required.');
      window.dispatchEvent(new Event('settings-changed'));
      window.dispatchEvent(new Event('vm-session-changed'));
    } catch (error) {
      this.err.set(true);
      this.msg.set(error instanceof Error ? error.message : 'Failed to select property.');
    } finally {
      this.busy.set(false);
    }
  }
}
